import React, {useRef, useCallback} from 'react';
import {flexRender, Header as TanStackHeader, Table as TanStackTable} from '@tanstack/react-table';
import {StyledThead, StyledTh, HeaderContent, HeaderLabel, SortIcon, ResizeHandle} from './styles';

interface HeaderProps {
  headerGroups: any[];
  table: TanStackTable<any>;
  resizable?: boolean;
}

export const Header: React.FC<HeaderProps> = ({headerGroups, table, resizable}) => {
  const wasResizingRef = useRef(false);

  const handleResizeStart = useCallback(
    (header: TanStackHeader<any, unknown>) => (e: React.MouseEvent | React.TouchEvent) => {
      wasResizingRef.current = true;
      header.getResizeHandler()(e);
    },
    [],
  );

  const handleThClick = useCallback(
    (header: TanStackHeader<any, unknown>) => (e: React.MouseEvent) => {
      if (wasResizingRef.current) {
        wasResizingRef.current = false;
        return;
      }
      const handler = header.column.getToggleSortingHandler();
      if (handler) handler(e);
    },
    [],
  );

  return (
    <StyledThead>
      {headerGroups.map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header: TanStackHeader<any, unknown>) => {
            const canSort = header.column.getCanSort();
            const sortDirection = header.column.getIsSorted();

            return (
              <StyledTh
                key={header.id}
                $canSort={canSort}
                onClick={canSort ? (resizable ? handleThClick(header) : header.column.getToggleSortingHandler()) : undefined}
                style={{
                  width: resizable ? header.getSize() : header.getSize() !== 150 ? header.getSize() : undefined,
                }}
              >
                {header.isPlaceholder ? null : (
                  <HeaderContent>
                    <HeaderLabel>{flexRender(header.column.columnDef.header, header.getContext())}</HeaderLabel>
                    {canSort && (
                      <SortIcon $direction={sortDirection || false}>
                        {sortDirection === 'asc' && '▲'}
                        {sortDirection === 'desc' && '▼'}
                        {!sortDirection && '⬍'}
                      </SortIcon>
                    )}
                  </HeaderContent>
                )}
                {resizable && header.column.getCanResize() && (
                  <ResizeHandle
                    onMouseDown={handleResizeStart(header)}
                    onTouchStart={handleResizeStart(header)}
                    onClick={(e) => e.stopPropagation()}
                    $isResizing={header.column.getIsResizing()}
                  />
                )}
              </StyledTh>
            );
          })}
        </tr>
      ))}
    </StyledThead>
  );
};
